import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalWrapper, Button, Prompt, DatePicker, Input } from "@/shared/ui";
import { FileUploader } from "@/shared/components";
import { extractErrorMessage, formatDateToISO, type Locale } from "@/shared/utils";
import { useCloseMedicalLeaveMutation } from "../api";

interface Props {
  leaveId: number;
  minDate?: Date | null;
  defaultEndDate?: Date | null;
  onClose: () => void;
  onSuccess: () => void;
}

export default function CloseMedicalLeaveModal({ leaveId, minDate, defaultEndDate, onClose, onSuccess }: Props) {
  const { t, i18n } = useTranslation("LeaveApplicationsPage");
  const locale = (i18n.language as Locale) || "ru";

  const [file, setFile] = useState<File | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate ?? null);
  const [noteNumber, setNoteNumber] = useState("");
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);

  const [closeMedicalLeave, { isLoading }] = useCloseMedicalLeaveMutation();

  const canSubmit = !!file && !!endDate && !!noteNumber.trim() && !isLoading;

  async function handleClose() {
    if (!canSubmit || !endDate) return;
    try {
      await closeMedicalLeave({
        id: leaveId,
        endDate: formatDateToISO(endDate),
        file: file ?? undefined,
        sickNoteNumber: noteNumber.trim() || undefined,
      }).unwrap();
      onSuccess();
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "actions.closeError", t),
        variant: "error",
      });
    }
  }

  return (
    <>
      {prompt && (
        <Prompt title={prompt.title} text={prompt.text} variant={prompt.variant} onClose={() => setPrompt(null)} namespace="LeaveApplicationsPage" />
      )}

      <ModalWrapper onClose={onClose} width="w-lg">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 border-b surface-base-stroke pb-3">
            <h4 className="text-display-2xs content-base-primary">{t("actions.closeMedicalLeave")}</h4>
            <p className="text-body-regular-sm content-base-secondary">{t("actions.closeMedicalLeaveHint")}</p>
          </div>

          <DatePicker
            locale={locale}
            mode="single"
            label={t("details.endDate")}
            hasRequiredLabel
            value={endDate}
            onChange={setEndDate}
            minDate={minDate ?? undefined}
          />

          <Input
            label={t("details.sickNoteNumber")}
            placeholder={t("placeholders.sickNoteNumber")}
            hasRequiredLabel
            value={noteNumber}
            onChange={(e) => setNoteNumber(e.target.value)}
          />

          <FileUploader
            accept=".pdf"
            maxSizeMB={10}
            value={file}
            onChange={setFile}
            hasRequiredLabel
            label={t("actions.selectSickNote")}
          />

          <div className="grid grid-cols-2 gap-3">
            <Button variant="secondary" size="md" onClick={onClose} disabled={isLoading}>
              {t("buttons.cancel")}
            </Button>
            <Button
              variant="primary"
              size="md"
              onClick={handleClose}
              disabled={!canSubmit}
              textDuringDisabled={!file || !endDate || !noteNumber.trim() ? t("rules.sickNoteAndDateRequired") : undefined}
              fullWidthTooltip
            >
              {isLoading ? t("actions.closing") : t("actions.closeAction")}
            </Button>
          </div>
        </div>
      </ModalWrapper>
    </>
  );
}
